import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Eyebrow } from '../ui';

const TestimonialSlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      text: "Heavily surprised and pretty happy too, the review told me everything I needed to know. I especially like that it gives suggested target audiences and regions for success.",
      author: "Sam",
      role: "Music Artist",
      initial: "S",
      gradient: "from-apple-blue to-apple-purple"
    },
    {
      id: 2,
      text: "The analysis was extremely detailed and very helpful. It also gave me an idea of the direction I should take in my future work!",
      author: "Matoi",
      role: "Music Artist",
      initial: "M",
      gradient: "from-apple-green to-apple-teal"
    },
    {
      id: 3,
      text: "Honestly dude I really like this tool. I know I said it before, but I really think you struck gold!",
      author: "Javi",
      role: "Canadian Musician",
      initial: "J",
      gradient: "from-apple-blue to-apple-purple"
    },
    {
      id: 4,
      text: "I like the similar artists part the most. That could help with SubmitHub, etc.",
      author: "Cade",
      role: "American Producer",
      initial: "C",
      gradient: "from-apple-green to-apple-teal"
    },
    {
      id: 5,
      text: "I'm staggered by just how good this analysis site is. At this stage I really can't think of any improvements that could be made. Keep up the good work.",
      author: "Nigel",
      role: "Spanish Producer",
      initial: "N",
      gradient: "from-apple-purple to-apple-pink"
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section className="mt-20 md:mt-24 border-t border-white/[0.06] pt-14 md:pt-16" aria-labelledby="testimonials-heading">
      <div className="text-center max-w-2xl mx-auto mb-10 px-2">
        <Eyebrow className="mb-4">Voices</Eyebrow>
        <h2 id="testimonials-heading" className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-gradient-insight tracking-tight leading-tight">
          What creators are saying
        </h2>
        <p className="mt-3 text-sm text-critic-text-secondary font-normal leading-relaxed">
          Real feedback from artists using CriticAI.
        </p>
      </div>

      <div
        className="relative max-w-3xl mx-auto px-1"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        <div className="relative overflow-hidden rounded-2xl">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="w-full flex-shrink-0 px-1">
                <div className="rounded-2xl border border-white/[0.08] bg-critic-bg-card/40 backdrop-blur-xl p-8 md:p-10 text-center shadow-[0_24px_80px_rgba(0,0,0,0.25)]">
                  <div className="flex justify-center mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${testimonial.gradient} rounded-2xl flex items-center justify-center shadow-lg border border-white/10`}>
                      <span className="text-white text-lg font-bold font-display">{testimonial.initial}</span>
                    </div>
                  </div>
                  <blockquote className="text-critic-text-primary text-base sm:text-lg leading-relaxed font-normal max-w-2xl mx-auto mb-6">
                    <span className="text-critic-text-muted/90">&ldquo;</span>
                    {testimonial.text}
                    <span className="text-critic-text-muted/90">&rdquo;</span>
                  </blockquote>
                  <footer className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2">
                    <cite className="font-display font-semibold text-critic-text-primary not-italic">
                      {testimonial.author}
                    </cite>
                    <span className="hidden sm:inline text-critic-text-muted" aria-hidden>
                      ·
                    </span>
                    <span className="text-sm text-critic-text-secondary font-normal">{testimonial.role}</span>
                  </footer>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={goToPrevious}
          className="absolute left-0 sm:left-1 top-1/2 -translate-y-1/2 rounded-full p-2.5 border border-white/[0.12] bg-black/40 backdrop-blur-md hover:bg-white/[0.08] hover:border-white/[0.2] transition-all duration-200 focus-visible:ring-2 focus-visible:ring-apple-blue focus-visible:ring-offset-2 focus-visible:ring-offset-critic-bg-deep"
          aria-label="Previous testimonial"
        >
          <ChevronLeft className="h-5 w-5 text-critic-text-secondary" />
        </button>

        <button
          type="button"
          onClick={goToNext}
          className="absolute right-0 sm:right-1 top-1/2 -translate-y-1/2 rounded-full p-2.5 border border-white/[0.12] bg-black/40 backdrop-blur-md hover:bg-white/[0.08] hover:border-white/[0.2] transition-all duration-200 focus-visible:ring-2 focus-visible:ring-apple-blue focus-visible:ring-offset-2 focus-visible:ring-offset-critic-bg-deep"
          aria-label="Next testimonial"
        >
          <ChevronRight className="h-5 w-5 text-critic-text-secondary" />
        </button>

        <div className="flex justify-center gap-2 mt-8" aria-label="Testimonial slides">
          {testimonials.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 focus-visible:ring-2 focus-visible:ring-apple-blue focus-visible:ring-offset-2 focus-visible:ring-offset-critic-bg-deep ${
                index === currentSlide
                  ? 'w-8 bg-apple-blue'
                  : 'w-2 bg-white/20 hover:bg-white/35'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : undefined}
            />
          ))}
        </div>

        <p className="text-center mt-4 text-xs text-critic-text-muted font-normal tabular-nums">
          {isAutoPlaying ? 'Rotating automatically · hover to pause' : 'Paused'}
        </p>
      </div>
    </section>
  );
};

export default TestimonialSlideshow;
